import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import Ellipsis from '@wix/da-ds/pkg/Icons/Ellipsis';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import type { BIEventPayload } from '@wix/da-bi/pkg/types';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';

export interface Props {
  user: PapiUser;
  className?: string;
  removeAll: (biData: BIEventPayload) => void;
}

const WatchedUserMenu: React.FC<Props> = ({ user, className, removeAll }) => {
  const isDyw3 = useFlag('dyw3');
  const biData = useContext(BiContext);
  const { t } = useTranslation();

  const dropdownItems = useMemo(() => {
    return [
      !isDyw3 && {
        label: t('subnavigation.button.userDropdown.items.visitProfile'),
        link: Url.userLink(user.username),
        biData: {
          itemid: user.userId,
          typeid: ResourceType.USER,
          evid: BiEventType.USERNAME_CLICK,
          sectionname: 'button',
        },
      },
      isDyw3 && {
        label: t('subnavigation.button.userDropdown.items.removeAll'),
        biData: {
          evid: BiEventType.WATCH_ITEM_REMOVE_CLICK,
          sectionname: 'button',
        },
        onClick: () => removeAll({ ...biData, sectionname: 'button' }),
      },
    ];
  }, [isDyw3, t, user.username, user.userId, removeAll, biData]);

  return (
    <IconButtonWithContextMenu
      items={dropdownItems}
      className={className}
      onOpenBiData={{
        evid: BiEventType.MORE_MENU_CLICK,
        typeid: ResourceType.USER,
        itemid: user.userId,
      }}
      withButton
      usePortal={false}
    >
      <Ellipsis size={IconSize.MEDIUM} />
    </IconButtonWithContextMenu>
  );
};
WatchedUserMenu.displayName = 'WatchedUserMenu';

export default WatchedUserMenu;
